/**
 * PATH: apps/quote/src/components/builder/sheets/ExcelAssumptionsContent.tsx
 * FILE_ID: QB-FE-EXCEL-ASSUMPTIONS-CONTENT-001
 * SIZE JUSTIFICATION (~343L vs 300L cap):
 *   File houses three tightly-coupled subtables (PerLineComments,
 *   WorksProgram, GroutCompartments) that all share the CollapsibleSection
 *   shell and the same Excel-Assumptions parser shape. Splitting each
 *   subtable into its own file would scatter ~30 lines of unique JSX
 *   per file and force three new imports for one render path — net
 *   negative for review. Re-evaluate when a fourth subtable lands.
 * PURPOSE: Render the Excel "Assumptions" worksheet content (per-line
 *          comments, Works Program, Grout Compartments) inside the
 *          AssumptionsSheet.
 * RELATED: QB-FE-ASSUMPTIONS-PARSER-001
 */

import { ChevronDown, ChevronRight, FileText, Calendar, Layers, FileSpreadsheet } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import type { QuoteDetail } from '@/lib/quoteApi'
import {
  parseAssumptionsContent,
  type PerLineComment,
  type Provenance,
  type WorksProgramZone,
  type GroutCompartmentRow,
} from '../panels/assumptions-content-parser'

interface ExcelAssumptionsContentProps {
  quote: QuoteDetail | null
}

function ProvenanceCard({ provenance }: { provenance: Provenance }) {
  // MR E (typed Assumptions): surface WHERE these typed values came
  // from. Estimators reading the right-rail panels want to trust the
  // numbers; showing the source workbook + sheet up-front turns
  // "where did this come from?" into a single-glance answer.
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-indigo-50/60 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 mb-3">
      <FileSpreadsheet className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
      <div className="text-xs leading-tight">
        <div className="font-medium text-indigo-800 dark:text-indigo-200">
          Source · {provenance.source}
        </div>
        <div className="text-indigo-600 dark:text-indigo-400 mt-0.5">
          Sheet: {provenance.sheet}
        </div>
      </div>
    </div>
  )
}

function CollapsibleSection({
  title,
  icon: Icon,
  description,
  defaultExpanded = true,
  children,
}: {
  title: string
  icon: React.ComponentType<{ className?: string }>
  description?: string
  defaultExpanded?: boolean
  children: React.ReactNode
}) {
  const [expanded, setExpanded] = useState(defaultExpanded)
  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'w-full flex items-center justify-between px-4 py-3',
          'bg-indigo-100 dark:bg-indigo-900/30',
          'text-indigo-800 dark:text-indigo-300',
          'border-indigo-200 dark:border-indigo-800',
        )}
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          <Icon className="w-4 h-4" />
          <span className="font-semibold">{title}</span>
          {description && (
            <span className="text-xs opacity-70">• {description}</span>
          )}
        </div>
      </button>
      {expanded && (
        <div className="bg-white dark:bg-slate-900 p-4">{children}</div>
      )}
    </div>
  )
}

function PerLineCommentsTable({ comments }: { comments: PerLineComment[] }) {
  if (comments.length === 0) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400 italic">
        No per-line commentary recorded.
      </p>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800/50">
          <tr className="text-slate-600 dark:text-slate-400">
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider">
              Section
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider">
              Line Item
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider">
              Comment
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {comments.map((c, idx) => (
            <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
              <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-300 align-top">
                {c.section}
              </td>
              <td className="px-3 py-2 text-sm font-medium text-slate-900 dark:text-white align-top">
                {c.lineItem || '—'}
              </td>
              <td className="px-3 py-2 text-sm text-slate-700 dark:text-slate-300">
                {c.comment}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function WorksProgramTable({
  zones,
  totalWeeks,
  totalDays,
  percentEscalation,
}: {
  zones: WorksProgramZone[]
  totalWeeks: number | null
  totalDays: number | null
  percentEscalation: number | null
}) {
  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800/50">
            <tr className="text-slate-600 dark:text-slate-400 text-xs font-medium">
              <th className="px-3 py-2 text-left">Zone</th>
              <th className="px-3 py-2 text-left">Start</th>
              <th className="px-3 py-2 text-left">End</th>
              <th className="px-3 py-2 text-right">Weeks</th>
              <th className="px-3 py-2 text-right">Pre 1/7/25</th>
              <th className="px-3 py-2 text-right">Post 1/7/25</th>
              <th className="px-3 py-2 text-right">% Post</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {zones.map((z) => (
              <tr key={z.zone} className="hover:bg-slate-50 dark:hover:bg-slate-800/30">
                <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">{z.zone}</td>
                <td className="px-3 py-2 text-slate-700 dark:text-slate-300 font-mono text-xs">
                  {z.start.slice(0, 10)}
                </td>
                <td className="px-3 py-2 text-slate-700 dark:text-slate-300 font-mono text-xs">
                  {z.end.slice(0, 10)}
                </td>
                <td className="px-3 py-2 text-right font-mono">{z.weeks.toFixed(1)}</td>
                <td className="px-3 py-2 text-right font-mono">{z.weeksPre.toFixed(1)}</td>
                <td className="px-3 py-2 text-right font-mono">{z.weeksPost.toFixed(1)}</td>
                <td className="px-3 py-2 text-right font-mono text-amber-600 dark:text-amber-400">
                  {(z.percentPost * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap gap-4 text-sm bg-slate-50 dark:bg-slate-800/30 rounded px-3 py-2">
        {totalWeeks !== null && (
          <div>
            <span className="text-slate-500 dark:text-slate-400">Total Weeks:</span>{' '}
            <span className="font-mono font-semibold">{totalWeeks}</span>
          </div>
        )}
        {totalDays !== null && (
          <div>
            <span className="text-slate-500 dark:text-slate-400">Total Days:</span>{' '}
            <span className="font-mono font-semibold">{totalDays}</span>
          </div>
        )}
        {percentEscalation !== null && (
          <div>
            <span className="text-slate-500 dark:text-slate-400">% Requiring Escalation:</span>{' '}
            <span className="font-mono font-semibold text-amber-600 dark:text-amber-400">
              {(percentEscalation * 100).toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

function GroutTable({
  title,
  rows,
  totals,
}: {
  title: string
  rows: GroutCompartmentRow[]
  totals: { hoseLength: number; boxes: number; flanges: number }
}) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">{title}</h4>
      <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 dark:bg-slate-800/50">
            <tr className="text-slate-600 dark:text-slate-400">
              <th className="px-2 py-1.5 text-left">Type</th>
              <th className="px-2 py-1.5 text-right">Number</th>
              <th className="px-2 py-1.5 text-right">Hose (m)</th>
              <th className="px-2 py-1.5 text-right">Hose Total</th>
              <th className="px-2 py-1.5 text-right">Box/Comp</th>
              <th className="px-2 py-1.5 text-right">Box Total</th>
              <th className="px-2 py-1.5 text-right">Flange/Comp</th>
              <th className="px-2 py-1.5 text-right">Flange Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {rows.map((r) => (
              <tr key={r.typeLabel}>
                <td className="px-2 py-1.5 text-slate-900 dark:text-white">{r.typeLabel}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.number}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.hoseLengthPerUnit}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.hoseLengthTotal}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.boxesPerCompartment}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.boxesTotal}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.flangesPerCompartment}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.flangesTotal}</td>
              </tr>
            ))}
            <tr className="bg-slate-50 dark:bg-slate-800/50 font-semibold">
              <td className="px-2 py-1.5 text-slate-900 dark:text-white">Totals</td>
              <td className="px-2 py-1.5"></td>
              <td className="px-2 py-1.5"></td>
              <td className="px-2 py-1.5 text-right font-mono">{totals.hoseLength}</td>
              <td className="px-2 py-1.5"></td>
              <td className="px-2 py-1.5 text-right font-mono">{totals.boxes}</td>
              <td className="px-2 py-1.5"></td>
              <td className="px-2 py-1.5 text-right font-mono">{totals.flanges}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function ExcelAssumptionsContent({
  quote,
}: ExcelAssumptionsContentProps) {
  const content = parseAssumptionsContent(quote?.config)
  if (!content) return null

  const hasAny =
    content.perLineComments.length > 0 ||
    (content.worksProgram?.zones?.length ?? 0) > 0 ||
    content.groutCompartments?.dpk4705?.rows.length ||
    content.groutCompartments?.dpk4706?.rows.length
  if (!hasAny) return null

  return (
    <div className="space-y-4">
      <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
        <h3 className="text-base font-bold text-slate-900 dark:text-white mb-3">
          Excel Assumptions Sheet Content
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
          Mirrors the &quot;Assumptions&quot; tab from the source .xlsm — per-line
          commentary, works program, and grout compartments.
        </p>
        {content.provenance && <ProvenanceCard provenance={content.provenance} />}
      </div>

      {content.perLineComments.length > 0 && (
        <CollapsibleSection
          title="Per-Line Commentary"
          icon={FileText}
          description={`${content.perLineComments.length} note(s)`}
        >
          <PerLineCommentsTable comments={content.perLineComments} />
        </CollapsibleSection>
      )}

      {content.worksProgram && content.worksProgram.zones.length > 0 && (
        <CollapsibleSection
          title="Works Program"
          icon={Calendar}
          description={`${content.worksProgram.zones.length} zone(s) · escalation breakdown`}
        >
          <WorksProgramTable
            zones={content.worksProgram.zones}
            totalWeeks={content.worksProgram.totalWeeks}
            totalDays={content.worksProgram.totalDays}
            percentEscalation={content.worksProgram.percentRequiringEscalation}
          />
        </CollapsibleSection>
      )}

      {content.groutCompartments &&
        (content.groutCompartments.dpk4705?.rows.length ||
          content.groutCompartments.dpk4706?.rows.length) ? (
        <CollapsibleSection
          title="Grout Compartments"
          icon={Layers}
          description="DPK 4705 + DPK 4706 quantities"
        >
          <div className="space-y-4">
            {content.groutCompartments.dpk4705 &&
              content.groutCompartments.dpk4705.rows.length > 0 && (
                <GroutTable
                  title="DPK 4705"
                  rows={content.groutCompartments.dpk4705.rows}
                  totals={content.groutCompartments.dpk4705.totals}
                />
              )}
            {content.groutCompartments.dpk4706 &&
              content.groutCompartments.dpk4706.rows.length > 0 && (
                <GroutTable
                  title="DPK 4706"
                  rows={content.groutCompartments.dpk4706.rows}
                  totals={content.groutCompartments.dpk4706.totals}
                />
              )}
            {content.groutCompartments.grandTotals && (
              <div className="flex flex-wrap gap-4 text-sm bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded px-3 py-2">
                <div className="font-semibold text-emerald-800 dark:text-emerald-200">
                  Grand Totals:
                </div>
                <div>
                  <span className="text-emerald-600 dark:text-emerald-400">Hose:</span>{' '}
                  <span className="font-mono font-bold">{content.groutCompartments.grandTotals.hoseLength}m</span>
                </div>
                <div>
                  <span className="text-emerald-600 dark:text-emerald-400">Boxes:</span>{' '}
                  <span className="font-mono font-bold">{content.groutCompartments.grandTotals.boxes}</span>
                </div>
                <div>
                  <span className="text-emerald-600 dark:text-emerald-400">Flanges:</span>{' '}
                  <span className="font-mono font-bold">{content.groutCompartments.grandTotals.flanges}</span>
                </div>
              </div>
            )}
          </div>
        </CollapsibleSection>
      ) : null}
    </div>
  )
}
